import { Gift, Loader2, Package, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { fetchSubpackTiers } from '@/api/member';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { fmtUsdAmount as usd } from '@/lib/money';

/** Shown until the live tiers load (or if they cannot be read). */
export const PIF_OPTIONS = [{ value: 'position', label: 'Single Position', price: 1 }];

/**
 * The gift choices come from the LIVE tier ladder (/api/me/subpacks/tiers), so any
 * configured pack — including cents prices such as $4.96 — can be given. Each pack
 * is sent as its exact price in cents: `subpack_{cents}c`.
 */
const optionsFrom = (data) => [
  { value: 'position', label: 'Single Position', price: Number(data.position_price_usd) || 1 },
  ...(data.tiers || []).map((t) => {
    const price = Number(t.tier_price_usd);
    // A Speedy Pack can only be given to a member who holds an active 1-Year Sub Pack (the API refuses otherwise).
    const speedy = t.requires_one_year_sub_pack === true;
    return { value: `subpack_${Math.round(price * 100)}c`, label: `$${usd(price)} ${speedy ? 'Speedy Pack' : 'Sub Pack'}`, price, speedy };
  }),
];

const FUND_SOURCES = [
  { value: 'purchase_balance', label: 'Purchase Balance' },
  { value: 'withdrawal_balance', label: 'Withdrawal Balance' },
];

/**
 * "Pay It Forward" / "Adopt via PIF" action button.
 *
 * Opens a Shadcn Dialog (which uses Radix Portal under the hood, rendering
 * directly under <body>) — this is essential because the previous inline
 * dropdown got clipped by table overflow-x-auto on mobile. The dialog
 * escapes ALL overflow / z-index constraints by design.
 *
 * Calls onSubmit(item, fund_source) and surfaces backend errors via toast.
 */
const PIFAction = ({ onSubmit, label = 'Pay It Forward', testid }) => {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState('position');
  const [fundSource, setFundSource] = useState('purchase_balance');
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState(PIF_OPTIONS);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    fetchSubpackTiers()
      .then((data) => {
        if (alive && data) setOptions(optionsFrom(data));
      })
      .catch(() => {
        /* keep the single-position fallback */
      });
    return () => {
      alive = false;
    };
  }, [open]);

  const selected = options.find((o) => o.value === item) || options[0];

  const handle = async () => {
    setBusy(true);
    try {
      await onSubmit(item, fundSource);
      setOpen(false);
      toast.success('PIF executed');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`PIF failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-b from-[#FFC20E] to-[#FFB612] text-[#0a0a0b] font-semibold text-[11px] uppercase tracking-[0.06em] border border-[#FFC20E] shadow-[0_8px_22px_-8px_rgba(255,182,18,0.55)] hover:brightness-105 transition"
          data-testid={testid || 'pif-action-trigger'}
        >
          <Gift className="w-3.5 h-3.5" />
          {label}
        </button>
      </DialogTrigger>
      <DialogContent className="sc-glass-strong max-w-md border-[rgba(255,182,18,0.32)] text-[var(--sc-text-bright)]" data-testid="pif-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-lg flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#FFC20E]" />
            {label}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--sc-text-dim)] leading-snug">
            Choose what to give and which wallet to charge. Funds leave you; the position(s) drop into the recipient&apos;s account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Item picker — radio-grid */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] mb-2 flex items-center gap-1.5">
              <Package className="w-3 h-3 text-[#FFC20E]" />
              Gift contents
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
              {options.map((o) => {
                const isSelected = item === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setItem(o.value)}
                    className="text-left px-3 py-2 rounded-lg border transition"
                    style={{
                      background: isSelected ? 'linear-gradient(135deg, rgba(255,194,14,0.18), rgba(255,182,18,0.08))' : 'rgba(0,0,0,0.32)',
                      borderColor: isSelected ? 'rgba(255,194,14,0.85)' : 'rgba(255,182,18,0.18)',
                    }}
                    data-testid={`pif-option-${o.value}`}
                  >
                    <div className="text-[12px] font-semibold" style={{ color: isSelected ? '#FFC20E' : '#e7e7e7' }}>
                      {o.label}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mt-0.5">${usd(o.price)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fund source */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] mb-2 flex items-center gap-1.5">
              <Wallet className="w-3 h-3 text-[#FFC20E]" />
              Pay from
            </div>
            <div className="grid grid-cols-2 gap-2">
              {FUND_SOURCES.map((s) => {
                const isSelected = fundSource === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setFundSource(s.value)}
                    className="px-3 py-2 rounded-lg border text-[11px] uppercase tracking-[0.12em] transition"
                    style={{
                      background: isSelected ? 'linear-gradient(135deg, #FFC20E, #FFB612)' : 'rgba(0,0,0,0.32)',
                      color: isSelected ? '#0a0a0b' : 'var(--sc-text-mute)',
                      borderColor: isSelected ? 'rgba(255,194,14,0.85)' : 'rgba(255,182,18,0.18)',
                      fontWeight: isSelected ? 700 : 500,
                    }}
                    data-testid={`pif-source-${s.value}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary line */}
          <div
            className="rounded-lg border p-3 text-[12px] leading-snug"
            style={{
              background: 'rgba(0,0,0,0.32)',
              borderColor: 'rgba(255,182,18,0.18)',
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">Summary</div>
            <div className="text-[var(--sc-text-bright)] mt-1">
              Giving <strong>{selected.label}</strong> (<strong className="sc-gold-text">${usd(selected.price)}</strong>), charging your{' '}
              <strong>{fundSource === 'purchase_balance' ? 'Purchase Balance' : 'Withdrawal Balance'}</strong>.
            </div>
            {selected.speedy && (
              <div className="mt-2 text-[11px] text-[#FFE08A]" data-testid="pif-speedy-requirement">
                A Speedy Pack can only be given to a member who already has an active 1-Year Sub Pack. If they don&apos;t have one yet, give a 1-Year Sub Pack
                first.
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => !busy && setOpen(false)}
              disabled={busy}
              className="px-3 py-2 rounded-lg border text-[12px] uppercase tracking-[0.16em] text-[var(--sc-text-mute)] disabled:opacity-50"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255,182,18,0.18)',
              }}
              data-testid="pif-action-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handle}
              disabled={busy}
              className="px-4 py-2 rounded-lg font-bold text-[12px] uppercase tracking-[0.16em] text-black disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
                border: '1px solid rgba(255,182,18,0.85)',
              }}
              data-testid="pif-action-execute"
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Working…
                </span>
              ) : (
                'Execute PIF'
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PIFAction;
